import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import queryString from 'query-string'
import dayjs, { Dayjs } from 'dayjs'
import { Button, Card, Grid } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'

import { ContentWrapper } from '../../../../components/ContentWrapper'
import { ExpenseFormModal } from '../../components/ExpenseFormModal'
import useAxios, { axiosInstance } from '../../../../hooks/useAxios'
import { PaginationResponseInterface } from '../../../../types/pagination'
import ExpensesTable from '../../components/ExpensesTable'
import { IExpense, IExpenseProduct } from '../../../../types/expense'
import Helmet from '../../../../components/Helmet'
import HeaderContent from '../../../../components/Layouts/HeaderContent'
import PageUrls from '../../../../constants/page-urls'
import ExpensesFilter, { IFilterMapped } from '../../components/ExpensesFilter'
import { MAX_DONWLOAD_PAGE_SIZE } from '../../../../constants/global'
import { message } from 'antd'
import * as XLSX from 'xlsx'

const Menus = [
  {
    breadcrumbName: 'รายการค่าใช้จ่าย',
    title: 'รายการค่าใช้จ่าย',
    path: PageUrls.Expenses,
  },
]

const ExpensesListPage = () => {
  const navigate = useNavigate()
  const queryStr = queryString.parse(window.location.search)
  const [isModalCreateExpense, setIsModalCreateExpense] =
    useState<boolean>(false)
  const [filter, setFilter] = useState<IFilterMapped>({
    page: Number(queryStr.page ?? 1),
    perPage: 15,
    name: queryStr.name?.length ? String(queryStr.name) : undefined,
    distributionChannel: queryStr.distributionChannel?.length
      ? String(queryStr.distributionChannel)
      : undefined,
    startDate: queryStr.startDate
      ? dayjs(String(queryStr.startDate)).startOf('day').format()
      : undefined,
    endDate: queryStr.endDate
      ? dayjs(String(queryStr.endDate)).endOf('day').format()
      : undefined,
  })

  const onFilterChange = useCallback(
    (newFilter: IFilterMapped) => {
      const filter = {
        ...newFilter,
        page: 1,
      }
      const newQuery = queryString.stringify({ ...filter })
      navigate(`${PageUrls.Expenses}?${newQuery}`, {
        replace: true,
      })
      setFilter(filter)
    },
    [filter],
  )

  const onPageChange = useCallback(
    (newFilter: IFilterMapped) => {
      const newQuery = queryString.stringify({ ...newFilter })
      navigate(`${PageUrls.Expenses}?${newQuery}`, {
        replace: true,
      })
      setFilter(newFilter)
    },
    [filter],
  )

  useEffect(() => {
    onFilterChange(filter)
  }, [])

  const expenseList = useAxios<PaginationResponseInterface<IExpense>>({
    method: 'GET',
    url: '/costs',
    params: filter,
  })

  const handleOnDownload = useCallback(
    async (date: Dayjs) => {
      let loop = true
      let page = 1,
        perPage = MAX_DONWLOAD_PAGE_SIZE
      const data: IExpense[] = []
      let startDate = filter?.startDate
      let endDate = filter?.endDate

      if (
        !startDate ||
        !endDate ||
        dayjs(endDate).diff(dayjs(startDate), 'day') > 31
      ) {
        startDate = date.startOf('month').format('YYYY-MM-DD')
        endDate = date.endOf('month').format('YYYY-MM-DD')
      }
      while (loop) {
        try {
          const result = await axiosInstance.get('/costs', {
            params: {
              ...filter,
              page,
              perPage,
              startDate,
              endDate,
            },
          })
          page++
          if (!result?.data?.records.length) {
            loop = false
            break
          }
          data.push(...result.data.records)
          if (result?.data?.count < perPage) {
            loop = false
            break
          }
        } catch (e) {
          message.warning('เกิดข้อผิดพลาดในการดาวน์โหลดข้อมูลบางส่วน')
          break
        }
      }
      if (!data.length) {
        message.warning('ไม่พบข้อมูล')
        return
      }
      const rows = data?.map((dt: IExpense) => {
        return {
          ช่องทาง: dt.name,
          ชื่อ: dt.distribution.name,
          url: dt.distribution.url,
          สินค้า: dt.products
            ?.map((item: IExpenseProduct) => {
              return item.name
            })
            .join(', '),
          'ราคา (บาท)': dt.amount,
          หมายเหตุ: dt.note,
          วันที่เพิ่ม: dayjs(dt.createdAt).format('DD/MM/YYYY HH:mm'),
          วันที่แก้ไข: dayjs(dt.updatedAt).format('DD/MM/YYYY HH:mm'),
        }
      })
      const ws = XLSX.utils.json_to_sheet(rows)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1')
      XLSX.writeFile(wb, `expenses.xlsx`)
      message.success('ดาวน์โหลดสำเร็จ')
    },
    [filter],
  )

  const onUpdateExpensesSuccess = useCallback(async () => {
    try {
      await expenseList.mutate()
    } catch (error) {}
  }, [filter])

  return (
    <ContentWrapper>
      <Helmet title="รายการค่าใช้จ่าย" />
      <ExpenseFormModal
        isOpen={isModalCreateExpense}
        onClose={(visible: boolean) => setIsModalCreateExpense(visible)}
        onUpdateExpensesSuccess={onUpdateExpensesSuccess}
      />
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid item>
          <HeaderContent
            goBack
            title="รายการค่าใช้จ่าย"
            routes={Menus}
            descriptions="รายการค่าใช้จ่าย"
          />
        </Grid>
        <Grid item mb={2}>
          <Button
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
            onClick={() => setIsModalCreateExpense(true)}
          >
            ค่าใช้จ่าย
          </Button>
        </Grid>
      </Grid>
      <Card
        sx={{
          padding: 2,
        }}
      >
        <ExpensesFilter
          onFilterChange={onFilterChange}
          defaultFilter={filter}
          onDownload={handleOnDownload}
        />
        <ExpensesTable
          isLoading={expenseList.isLoading}
          data={expenseList.data?.records ?? []}
          onUpdateExpensesSuccess={onUpdateExpensesSuccess}
          filter={{ ...filter }}
          onFilterChange={onFilterChange}
          onPageChange={onPageChange}
          total={expenseList.data?.count ?? 0}
        />
      </Card>
    </ContentWrapper>
  )
}

export default ExpensesListPage
